import { AUDIT_STATUS, IMAGE_TYPE, PUBLISHED, SITE_TYPE } from './constants';
import { normalizePart, syncMainFlag } from './model';

const STORAGE_KEY = 'product-case-mock-list';

function createId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

function getBaseName(name) {
  return String(name || '').replace(/\.[^.]+$/, '') || '精密金属零件';
}

function nowText() {
  const date = new Date();
  const pad = value => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function isMockMode(route) {
  return route && route.query && route.query.mock === '1';
}

export function createMockUpload(file) {
  const hashCode = createId('mock-hash');
  return {
    hashCode,
    url: URL.createObjectURL(file),
    previewUrl: URL.createObjectURL(file),
    originalName: file.name,
    extentionName: file.name && file.name.split('.').pop(),
    size: `${Math.max(1, Math.round(file.size / 1024))}KB`
  };
}

export function createMockPartFromImage(image, siteType = SITE_TYPE.CN, index = 0) {
  const baseName = getBaseName(image.originalName);
  const originalHashCode = image.originalHashCode || image.hashCode;
  const processedHashCode = `${originalHashCode}-processed`;
  const imageItem = {
    id: null,
    clientImageId: image.clientImageId || createId('mock-img'),
    originalHashCode,
    processedHashCode,
    selectedHashCode: processedHashCode,
    selectedImageType: IMAGE_TYPE.PROCESSED,
    processStatus: 'success',
    processMessage: '',
    sort: 1,
    isMain: true,
    originalName: image.originalName,
    previewUrl: image.previewUrl,
    originalPreviewUrl: image.previewUrl,
    processedPreviewUrl: image.previewUrl
  };
  const part = {
    clientPartId: createId('mock-part'),
    id: null,
    siteType,
    sourceProductId: null,
    productGroupId: null,
    cnName: siteType === SITE_TYPE.CN ? `${baseName}零件` : '',
    enName: siteType === SITE_TYPE.INTL ? `${baseName} Precision Part` : '',
    cnContent: '该零件适用于非标设备、自动化工装和机械结构连接场景，支持按图定制加工，具备较好的结构稳定性和批量生产适配性。',
    enContent: 'This precision part is suitable for custom equipment, automation fixtures and mechanical assemblies. It supports drawing-based manufacturing and batch production.',
    keywords: siteType === SITE_TYPE.INTL ? ['precision part', 'custom machining', 'automation'] : ['精密零件', '非标定制', '机加工'],
    mainCategory: 'machining',
    mainCategoryName: siteType === SITE_TYPE.INTL ? 'Machining' : '机加工',
    subCategory: null,
    subCategoryName: '',
    material: 'aluminum',
    materialName: siteType === SITE_TYPE.INTL ? 'Aluminum Alloy' : '铝合金',
    priceRange: index % 2 === 0 ? '¥9.6~24' : '¥12~36',
    minOrderAmount: '10',
    minOrderUnit: '件',
    produceAbility: '5000',
    produceAbilityType: '月',
    productPictureList: [imageItem],
    defaultPhoto: processedHashCode,
    published: PUBLISHED.DRAFT,
    status: AUDIT_STATUS.REVIEWING,
    parseStatus: 'success',
    warnings: []
  };
  return syncMainFlag(part);
}

export function createMockParseResult(images, siteType) {
  return images.map((image, index) => normalizePart(createMockPartFromImage(image, siteType, index), siteType));
}

export function polishMockPart(part, siteType, refineField) {
  const next = normalizePart({ ...part }, siteType);
  const polishAll = !refineField;
  if (siteType === SITE_TYPE.INTL) {
    if (polishAll || refineField === 'name') next.enName = next.enName || `${next.cnName || 'Precision Part'} - AI Polished`;
    if (polishAll || refineField === 'content') next.enContent = 'This AI-polished product description highlights material, process, application scenarios and custom manufacturing capability for international buyers.';
    if (polishAll || refineField === 'keywords') next.keywords = ['AI polished', 'precision manufacturing', 'custom parts'];
  } else {
    if (polishAll || refineField === 'name') next.cnName = next.cnName ? `${next.cnName}` : 'AI识别精密零件';
    if (polishAll || refineField === 'content') next.cnContent = 'AI已根据全部零件图片补全描述：该零件适用于自动化设备、夹具底座和机械连接结构，支持来图定制、表面处理和中小批量生产。';
    if (polishAll || refineField === 'keywords') next.keywords = ['AI润色', '精密加工', '非标零件'];
  }
  next.mainCategory = next.mainCategory || 'machining';
  next.mainCategoryName = next.mainCategoryName || (siteType === SITE_TYPE.INTL ? 'Machining' : '机加工');
  next.material = next.material || 'aluminum';
  next.materialName = next.materialName || (siteType === SITE_TYPE.INTL ? 'Aluminum Alloy' : '铝合金');
  return syncMainFlag(next);
}

export function processMockImages(part, newImages, siteType) {
  const next = normalizePart({ ...part }, siteType);
  const additions = newImages.map((image, index) => {
    const originalHashCode = image.originalHashCode || image.hashCode;
    const processedHashCode = `${originalHashCode}-processed`;
    return {
      id: null,
      clientImageId: image.clientImageId || createId('mock-img'),
      originalHashCode,
      processedHashCode,
      selectedHashCode: processedHashCode,
      selectedImageType: IMAGE_TYPE.PROCESSED,
      processStatus: 'success',
      processMessage: '',
      sort: next.productPictureList.length + index + 1,
      isMain: false,
      originalName: image.originalName,
      previewUrl: image.previewUrl,
      originalPreviewUrl: image.previewUrl,
      processedPreviewUrl: image.previewUrl
    };
  });
  additions.forEach(image => {
    const index = next.productPictureList.findIndex(item => item.clientImageId === image.clientImageId || item.originalHashCode === image.originalHashCode);
    if (index > -1) next.productPictureList.splice(index, 1, image);
    else next.productPictureList.push(image);
  });
  next.productPictureList = next.productPictureList.slice(0, 5);
  if (!next.defaultPhoto && next.productPictureList[0]) next.defaultPhoto = next.productPictureList[0].selectedHashCode;
  return syncMainFlag(next);
}

export function readMockList(siteType) {
  try {
    const all = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
    return all.filter(item => Number(item.siteType) === Number(siteType));
  } catch (error) {
    return [];
  }
}

export function saveMockParts(parts, siteType, submit) {
  const current = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
  const time = nowText();
  const saved = parts.map((part, index) => {
    const next = normalizePart({ ...part }, siteType);
    next.id = next.id || Number(`${Date.now()}${index}`);
    next.siteType = siteType;
    next.published = submit ? PUBLISHED.SUBMITTED : PUBLISHED.DRAFT;
    next.status = AUDIT_STATUS.REVIEWING;
    next.createTime = next.createTime || time;
    next.updateTime = time;
    return next;
  });
  const savedIds = saved.map(item => item.id);
  const rest = current.filter(item => !savedIds.includes(item.id));
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...rest, ...saved]));
  return saved;
}

export function updateMockProduct(productId, updater) {
  const current = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
  const next = current.map(item => Number(item.id) === Number(productId) ? updater(item) : item);
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

export function deleteMockProduct(productId) {
  const current = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current.filter(item => Number(item.id) !== Number(productId))));
}
